import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllUserAction, getUserByIdAction, clearGetUserByIdAction, updateTenantUserStatusAction, clearUpdateTenantUserStatusAction } from "../../store/actions";
import {
    Box,
    Grid,
    Card,
    Typography,
    Button,
    TextField,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Paper,
    useMediaQuery,
    Pagination,
    PaginationItem,
} from "@mui/material";

import {
    People,
    PersonOutline,
    ShieldOutlined,
    TrendingUp,
    Search,
} from "@mui/icons-material";
import { EditOutlined, DeleteOutlined } from "@mui/icons-material";

const statsFromUsers = (users) => {
    const total = users.length;
    const active = users.filter(u => u.status === 'ACTIVE').length;
    const adminCount = users.filter(u => {
        const r = (u.role || '').toLowerCase();
        return r.includes('admin');
    }).length;

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const newThisMonth = users.filter(u => {
        const dateStr = u.createdAt || u.created_at || u.createdDate || u.created_on;
        if (!dateStr) return false;
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return false;
        return d >= startOfMonth;
    }).length;

    return [
        { label: "Total Users", value: total.toLocaleString(), icon: <People sx={{ color: '#2563EB' }} />, bg: "#E8F0FF" },
        { label: "Active Users", value: active.toLocaleString(), icon: <PersonOutline sx={{ color: '#059669' }} />, bg: "#E8FFF1" },
        { label: "Administrators", value: adminCount.toLocaleString(), icon: <ShieldOutlined sx={{ color: '#F59E42' }} />, bg: "#FFF3E0" },
        { label: "New This Month", value: newThisMonth.toLocaleString(), icon: <TrendingUp sx={{ color: '#A21CAF' }} />, bg: "#F3E8FF" },
    ];
};


export default function UserManagement() {
    // Handler for edit icon click
    const handleEditUser = (userId) => {
        // Only navigate, let edit page handle fetching user by id
        dispatch(clearGetUserByIdAction());
        navigate('/masterData/usercreate', { state: { userId, mode: 'edit' } });
    };
    const isMobile = useMediaQuery('(max-width:600px)');
    const isIpad = useMediaQuery('(min-width:600px) and (max-width:1100px)');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [excludedUserIds, setExcludedUserIds] = useState([]);

    const handleDeleteUser = (userId) => {
        dispatch(updateTenantUserStatusAction({ status: 'EXITED', endPoint: `tenant-user/${userId}/status` }));
        setExcludedUserIds((prev) => [...prev, userId]);
    };
    // Expecting API response shape: { data: { users: [...] } }
    const { successData, loading } = useSelector((state) => state.getAllUserReducer || {});
    const { successData: statusUpdateData } = useSelector((state) => state.updateTenantUserStatusReducer || {});
    // Defensive: support both array and object shape
    const users = useMemo(() => {
        if (Array.isArray(successData)) return successData;
        if (Array.isArray(successData?.data?.users)) return successData.data.users;
        return [];
    }, [successData]);

    //   const users = Array.isArray(successData)
    //     ? successData
    //     : (Array.isArray(successData?.data?.users)
    //         ? successData.data.users
    //         : []));

    const [searchQuery, setSearchQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState("ALL");
    const [statusFilter, setStatusFilter] = useState("ACTIVE");
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;

    const roleOptions = useMemo(() => {
        const roles = Array.from(new Set(users.map(u => u.role).filter(Boolean)));
        return ["ALL", ...roles];
    }, [users]);

    const statusOptions = ["ALL", "ACTIVE", "EXITED"];

    const filteredUsers = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return users.filter(u => {
            if (excludedUserIds.includes(u.userId)) return false;
            const matchesName = q ? (u.name || "").toLowerCase().includes(q) : true;
            const matchesRole = roleFilter === "ALL" ? true : (u.role === roleFilter);
            const matchesStatus = statusFilter === "ALL" ? true : (u.status === statusFilter);
            return matchesName && matchesRole && matchesStatus;
        });
    }, [users, searchQuery, roleFilter, statusFilter, excludedUserIds]);

    const totalPages = useMemo(() => {
        const pages = Math.ceil(filteredUsers.length / rowsPerPage);
        return Math.max(1, pages || 1);
    }, [filteredUsers.length]);

    useEffect(() => {
        // Reset page when filters/search change or when current page exceeds total pages
        setPage(1);
    }, [searchQuery, roleFilter, statusFilter]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(1);
        }
    }, [totalPages, page]);

    const paginatedUsers = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredUsers.slice(start, start + rowsPerPage);
    }, [filteredUsers, page]);

    const showingStart = filteredUsers.length === 0 ? 0 : ((page - 1) * rowsPerPage + 1);
    const showingEnd = Math.min(page * rowsPerPage, filteredUsers.length);

    useEffect(() => {
        if (statusUpdateData) {
            dispatch(clearUpdateTenantUserStatusAction());
        }
    }, [statusUpdateData, dispatch]);

    // Prevent duplicate getAllUserAction dispatches (React 18 StrictMode)
    const didFetchUsers = React.useRef(false);
    useEffect(() => {
        if (!didFetchUsers.current) {
            dispatch(getAllUserAction());
            didFetchUsers.current = true;
        }
    }, [dispatch]);

    return (
        <Box sx={{ minHeight: "100vh" }}>
            {/* Header */}
            <Box mb={3} mt={{ xs: 0, sm: 10, md: 0 }} >
                <Typography variant="h6" fontWeight={600}>
                    User Management
                </Typography>
            </Box>
            {/* Modern Stat Cards */}
            <Box sx={{ width: '100%', mb: 3 }}>
                <Grid
                    container
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{ width: '100%', m: 0 }}
                >
                    {statsFromUsers(users).map((s) => (
                        <Grid
                            item
                            size={{ xs: 6, sm: 3, md: 3 }}
                            key={s.label}
                            sx={{ p: 0, display: 'flex', justifyContent: 'stretch' }}
                        >
                            <Card sx={{ p: isIpad ? 1.5 : 2, borderRadius: 3, height: isIpad ? 60 : 50, width: '100%', mx: 0, display: 'flex', alignItems: 'center' }}>
                                <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontSize: { xs: '0.85rem', sm: 'inherit' }, textAlign: 'left', mb: 0 }}
                                        >
                                            {s.label}
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            fontWeight={600}
                                            sx={{ fontSize: { xs: '1.1rem', sm: isIpad ? '1.4rem' : '1.5rem' }, textAlign: 'left', mb: 0 }}
                                        >
                                            {s.value}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            bgcolor: s.bg,
                                            width: isIpad ? 44 : 45,
                                            height: isIpad ? 44 : 45,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 2,
                                        }}
                                    >
                                        {s.icon}
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Filters */}
            <Card sx={{ mr: { xs: 1 }, p: 2, borderRadius: 3, mb: 2 }}>
                <Grid container spacing={2} alignItems="center"  flexWrap="wrap">
                    <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search ..."
                            slotProps={{
                                input: {
                                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                                },
                            }}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                },
                            }}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </Grid>
                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select
                            size="small"
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            sx={{ width: '100%', borderRadius: 3 }}
                        >
                            {roleOptions.map(r => (
                                <MenuItem key={r} value={r}>{r === "ALL" ? "Role: All" : r}</MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select
                            size="small"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={{ width: '100%', borderRadius: 3 }}
                        >
                            {statusOptions.filter(s => s !== "ALL").map(s => (
                                <MenuItem key={s} value={s}>
                                    {s === "ACTIVE" ? "Status: Active" : "Status: Exited"}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                        <Grid 
                            item 
                            size={{ xs: 12, sm: 4, md: 2 }} 
                            sx={{ 
                                width: { xs: '100%', sm: 'auto' }, 
                                display: 'flex', 
                                justifyContent: { xs: 'stretch', md: 'flex-end' }, 
                                alignItems: 'center', 
                                ml: { xs: 0, md: 'auto' },
                                px: { xs: 0, sm: 0 },
                            }}
                        >
                            <Button
                                fullWidth={true}
                                variant="contained"
                                sx={{ 
                                    borderRadius: 2, 
                                    whiteSpace: 'nowrap', 
                                    width: { xs: '100%', sm: 'auto' },
                                    '@media (min-width:1100px) and (max-width:1500px)': { fontSize: '0.70rem' }
                                }}
                                onClick={() => navigate('/masterData/usercreate')}
                            >
                                + Add New User
                            </Button>
                        </Grid>
                </Grid>
            </Card>

            {/* Responsive List/Table styled  */}
            {isMobile ? (
                <Grid container spacing={2}>
                    {loading && <Typography>Loading users...</Typography>}
                    {!loading && paginatedUsers.map((u) => (
                        <Grid item size={{ xs: 12 }} key={u.userId || u.email || u.mobile}>
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    p: 1.5,
                                    border: '1px solid #E5E7EB',
                                    '&:hover': { border: '2px solid #2563EB', boxShadow: '0 4px 20px 0 rgba(37,99,235,0.10)' },
                                    position: 'relative',
                                }}
                            >
                                {/* Status at top right */}
                                <Box sx={{ position: 'absolute', top: 12, right: 18, zIndex: 1 }}>
                                    <Chip label={u.status === 'ACTIVE' ? 'Active' : 'Exited'} size="small" sx={{ backgroundColor: u.status === 'ACTIVE' ? '#D1FAE5' : '#FECACA', color: u.status === 'ACTIVE' ? '#047857' : '#B91C1C', fontWeight: 500, fontSize: 11, px: 1.5, width: 70, justifyContent: 'center' }} />
                                </Box>
                                {/* Card content */}
                                <Box>
                                    {/* User */}
                                    <Box mb={1} display="flex" alignItems="center" gap={1}>
                                        <Typography fontSize={12} fontWeight={600} minWidth={90}>User:</Typography>
                                        <Typography variant="body2">{u.name}</Typography>
                                    </Box>
                                    {/* Phone */}
                                    <Box mb={1} display="flex" alignItems="center" gap={1}>
                                        <Typography fontSize={12} fontWeight={600} minWidth={90}>Phone:</Typography>
                                        <Typography variant="body2">{u.mobile}</Typography>
                                    </Box>
                                    {/* Email */}
                                    {/* <Box mb={1} display="flex" alignItems="center" gap={1}>
                                        <Typography fontSize={12} fontWeight={600} minWidth={90}>Email:</Typography>
                                        <Typography variant="body2">{u.email}</Typography>
                                    </Box> */}
                                    {/* Role */}
                                    <Box mb={1} display="flex" alignItems="center" gap={1}>
                                        <Typography fontSize={12} fontWeight={600} minWidth={90}>Role:</Typography>
                                        <Typography variant="body2">{u.role}</Typography>
                                    </Box>
                                </Box>
                                {/* Actions at bottom right */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 2 }}>
                                    <IconButton size="small" onClick={() => handleEditUser(u.userId)}>
                                        <EditOutlined fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleDeleteUser(u.userId)}>
                                        <DeleteOutlined fontSize="small" sx={{ color: '#DC2626', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                    </IconButton>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                    <Grid item size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                Showing {showingStart}-{showingEnd} of {filteredUsers.length}
                            </Typography>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                size="small"
                                renderItem={(item) => (
                                    <PaginationItem
                                        {...item}
                                        sx={{
                                            borderRadius: '50%',
                                            minWidth: 32,
                                            height: 32,
                                            '&.Mui-selected': {
                                                backgroundColor: '#2563EB',
                                                color: '#FFFFFF',
                                            },
                                            '&.Mui-selected:hover': {
                                                backgroundColor: '#2563EB',
                                                color: '#FFFFFF',
                                            },
                                            
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Grid>
                </Grid>
            ) : (
                <Box>
                    {/* Table Header styled like Crop */}
                    <Box
                        display={{ xs: 'none', sm: 'grid' }}
                        gridTemplateColumns={{ sm: 'repeat(5, 1fr)' }}
                        px={{ xs: 1, sm: 2 }}
                        mb={{ xs: 1, sm: 1 }}
                    >
                        {[
                            'USER DETAILS',
                            'MOBILE NUMBER',
                            // 'EMAIL ADDRESS',
                            'ROLE TYPE',
                            'STATUS',
                            'ACTIONS',
                        ].map((label) => (
                            <Typography key={label} fontSize={{ sm: 8, md: 11 }} color="#000000" fontWeight={600}>
                                {label}
                            </Typography>
                        ))}
                    </Box>

                    {/* Rows styled like Crop */}
                    {loading && <Typography sx={{ gridColumn: '1/-1' }}>Loading users...</Typography>}
                    {!loading && paginatedUsers.map((u) => (
                        <Paper
                            key={u.userId || u.email || u.mobile}
                            elevation={0}
                            sx={{
                                mb: { xs: 1, sm: 2 },
                                p: { xs: 1, sm: 2 },
                                borderRadius: 3,
                                display: { xs: 'block', sm: 'grid' },
                                gridTemplateColumns: { sm: 'repeat(5, 1fr)' },
                                alignItems: 'center',
                                border: '1px solid #E5E7EB',
                                gap: { xs: 1.25, sm: 1.75 },
                                '&:hover': { border: '2px solid #2563EB', boxShadow: '0 4px 20px 0 rgba(37,99,235,0.10)' },
                            }}
                        >
                            {/* Desktop layout */}
                            <Box display={{ xs: 'none', sm: 'flex' }} gap={2} alignItems="center">
                                <Box>
                                    <Typography variant="body2">{u.name}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }} gap={1}>
                                <Typography variant="body2">{u.mobile}</Typography>
                            </Box>
                            {/* <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }} gap={1}>
                                <Typography variant="body2">{u.email}</Typography>
                            </Box> */}
                            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                                <Typography variant="body2">{u.role}</Typography>
                            </Box>
                            <Chip sx={{ display: { xs: 'none', sm: 'inline-flex' }, width: { sm: 64 }, backgroundColor: u.status === 'ACTIVE' ? '#D1FAE5' : '#FECACA', color: u.status === 'ACTIVE' ? '#047857' : '#B91C1C', fontSize: 12, fontWeight: 600 }} label={u.status === 'ACTIVE' ? 'Active' : 'Exited'} size="small" />
                            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
                                <IconButton size="small" onClick={() => handleEditUser(u.userId)}>
                                    <EditOutlined fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDeleteUser(u.userId)}>
                                    <DeleteOutlined fontSize="small" sx={{ color: '#DC2626', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                </IconButton>
                            </Box>
                        </Paper>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            Showing {showingStart}-{showingEnd} of {filteredUsers.length}
                        </Typography>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            size="small"
                            renderItem={(item) => (
                                <PaginationItem
                                    {...item}
                                    sx={{
                                        borderRadius: '50%',
                                        minWidth: 32,
                                        height: 32,
                                        '&.Mui-selected': {
                                            backgroundColor: '#2563EB',
                                            color: '#FFFFFF',
                                        },
                                        '&.Mui-selected:hover': {
                                            backgroundColor: '#2563EB',
                                            color: '#FFFFFF',
                                        },
                                        
                                    }}
                                />
                            )}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
}
